/**
 * Dashboard Job Card Component
 * Compact job card for dashboard "My Jobs" section
 * Shows saved/applied jobs with hover effect for navigation
 */

import React, { useState } from 'react';
import { MapPin } from 'lucide-react';
import { Job } from '../../../recruiter/services/jobApi';

interface DashboardJobCardProps {
    job: Job;
    status: 'saved' | 'applied';
    onViewClick: (jobId: string) => void;
}

const DashboardJobCard: React.FC<DashboardJobCardProps> = ({ job, status, onViewClick }) => {
    const [isHovered, setIsHovered] = useState(false);

    const jobId = job.id || job._id || '';

    // Format location
    const formatLocation = (location: any): string => {
        if (!location) return 'Remote';
        if (typeof location === 'string') return location;
        if (typeof location === 'object') {
            const { city, state, country } = location;
            return [city, state, country].filter(Boolean).join(', ') || 'Remote';
        }
        return 'Remote';
    };

    return (
        <div
            className="relative p-4 bg-white rounded-xl border border-[#EDEDED] cursor-pointer transition-all hover:border-[#015FA6] hover:shadow-sm"
            onMouseEnter={() => setIsHovered(true)}
            onMouseLeave={() => setIsHovered(false)}
            onClick={() => onViewClick(jobId)}
        >
            {/* Job info */}
            <div className="flex items-start gap-3">
                {/* Company Logo/Initial */}
                <div className="w-10 h-10 rounded-lg bg-gradient-to-br from-[#015FA6] to-[#6EABFF] flex items-center justify-center text-white font-semibold text-sm flex-shrink-0">
                    {job.companyName ? job.companyName.charAt(0).toUpperCase() : 'C'}
                </div>

                {/* Job Details */}
                <div className="flex-1 min-w-0">
                    <h4 className="font-semibold text-sm text-[#0B111E] truncate">{job.title}</h4>
                    <p className="text-xs text-[#515151] truncate">{job.companyName}</p>
                    <div className="flex items-center gap-1 mt-1 text-xs text-[#757575]">
                        <MapPin size={12} />
                        <span className="truncate">{formatLocation(job.location)}</span>
                    </div>
                </div>

                {/* Status Badge */}
                <span className={`px-2 py-1 text-xs font-medium rounded-full flex-shrink-0 ${
                    status === 'applied'
                        ? 'bg-[#E8F5E9] text-[#2E7D32]'
                        : 'bg-[#E3F2FD] text-[#015FA6]'
                }`}>
                    {status === 'applied' ? 'Applied' : 'Saved'}
                </span>
            </div>

            {/* Hover overlay */}
            {isHovered && (
                <div className="absolute inset-0 bg-white/90 backdrop-blur-sm rounded-xl flex items-center justify-center transition-all">
                    <span className="text-sm font-medium text-[#015FA6] hover:underline">
                        View in Browse Jobs
                    </span>
                </div>
            )}
        </div>
    );
};

export default DashboardJobCard;
